/* eslint-disable react/no-deprecated */
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import isEqual from 'react-fast-compare';
import { get, getRulesFromState, pageContext, pageRulesStateToProps, haveRulesChanged, interpolateString } from 'ctm-util-functions';
import classNames from 'classnames';
import { splitTrafficIdSelector } from 'ctm-selectors';
import { excludePageFromJourney } from 'ctm-util-functions/lib/excludePages';
import { getExperimentID } from 'ctm-app/client/store/selectors';
import { experimentIDEnum } from 'ctm-vertical-base/base/reducers/page';
import { getSelectedValues } from '../utils';

import { Button } from '../button/button';
import { LoadingAnimation } from '../loading_animation/loading_animation';
import styles from './next_step_button.css';

/**
 * _NextStepButton React Component
 * component used to progress in the journey and triggers the form validation.
 * @extends React.Component
 * @returns {Node} a Button Component
 */
class _NextStepButton extends Component {

    constructor(props) {
        super(props);
        const pageState = pageContext.getProps().pageState;
        this.pageName = props.pageName || pageState.pageName;
        this.contentProps = {
            icon: 'ctm_arrow_right'
        };
        this.defaultUIComponent = 'btn_secondary_xlarge_button_radius';
    }

    /**
     * Determine if the component should re-render based on the nextProps, the only change that should cause a re-render
     * is if the transitioningToNewPage property is different.
     * @param  {Object} nextProps  - The incoming set of props.
     * @param  {Object} nextState  - The incoming state.
     * @returns {Boolean}           - true if the component should re-render.
     */
    shouldComponentUpdate = (nextProps, nextState) => {
        const { transitioningToNewPage, isPageValid, elementTouched, selectedValues, experimentID } = this.props;
        return (
            transitioningToNewPage !== nextProps.transitioningToNewPage
            || isPageValid !== nextProps.isPageValid
            || haveRulesChanged(nextProps, this.props)
            || elementTouched !== nextProps.elementTouched
            || experimentID !== nextProps.experimentID
            // @todo: move haveSelectedValuesChanged from form utils and use it here as well
            // not a huge issue for now as there are only 2 buttons that have selectors
            || !isEqual(selectedValues, nextProps.selectedValues)
        );
    };

    /**
     * On each update; allow progression to the next step if this is an auto-progress button, on a valid page where
     * the controlling element has been touched.
     * @param {Object} nextProps - The incoming set of props
     * @param {Object} nextState - The incoming state
     */
    componentWillUpdate = (nextProps, nextState) => {
        const { autoProgress, elementTouched, validateAndProgress } = this.props;
        // This condition is used when auto-progress is based off a user interaction, such as clicking a badge
        let autoProgressCondition = autoProgress && nextProps.isPageValid && nextProps.elementTouched;
        if (get(this.props, 'rules.allowProgressIfTrue')) {
            // This condition is used for conditional progression, eg skipping pages etc
            autoProgressCondition = autoProgressCondition && elementTouched === false;
        }
        // This checks that you have an autoProgressCondition and your not currently transitioning to a new page
        if (autoProgressCondition && !nextProps.transitioningToNewPage) {
            if (get(nextProps, 'rules.autoProgress.result', true)) {
                validateAndProgress(nextProps);
            }
        }
    };

    /**
     * Reset the touches for the controlling auto-progress element, if it is defined.
     * This allows the user to go back to the page to select another option; also required for preload.
     */
    componentWillMount = () => {
        const { elementTouchName, dispatch } = this.props;
        if (elementTouchName) {
            dispatch({
                type: 'RESET_TOUCHES',
                identifiers: [elementTouchName],
                stateKey: 'vertical.quote' // @todo: get this from somewhere
            });
        }
    };

    /**
     * On click of the next step button:
     * * Prevent loading the link if this page is invalid
     * * Otherwise, trigger the validate and progress saga.
     * @param {Object} event - the onclick event
     */
    onClick = (event) => {
        const { onClickActions, dispatch, validateAndProgress } = this.props;
        if (get(this.props, 'rules.disabled.result')) {
            event.preventDefault();
            return;
        }

        if (onClickActions) {
            onClickActions.map((action) => dispatch(action));
        }

        validateAndProgress(this.props, event);
    };

    /**
     * If this button should show the loading animation and we're transitioning to a new page, return the
     * LoadingAnimation component, otherwise return null.
     * @returns {Node|null} - The LoadingAnimationComponent or null if the component isn't required.
     */
    getLoadingAnimation = () => {
        const { showLoadingAnimation, transitioningToNewPage } = this.props;
        if (showLoadingAnimation && transitioningToNewPage) {
            return <LoadingAnimation />;
        }
        return null;
    };

    /**
     * Renders the NextStepButton as an InternalLink with an optional LoadingAnimation component.
     * @returns {Object} - The NextStepButton component for the current page.
     */
    render = () => {
        const { autoProgressWithButton, autoProgress, hasSeenConfirmationPage } = this.props;

        // Don't render a button if the nextStepButton props in the form schema have autoProgress: true

        const canAutoProgressToPrefill = get(this.props, 'rules.autoProgressToPrefill.result');

        const { buttonText, selectedValues, variant, isPageValid, className, icon, experimentID } = this.props;

        let UIComponent = `${this.defaultUIComponent}_disabled`;
        let buttonClassName = '';
        if (isPageValid && !get(this.props, 'rules.disabled.result')) {
            UIComponent = this.defaultUIComponent;
            const customClassName = className ? styles[className] : null;
            buttonClassName = classNames(customClassName);
        }

        if (!hasSeenConfirmationPage && !autoProgressWithButton && autoProgress && get(this.props, 'rules.autoProgress.result', true)) {
            return null;
        }

        let label = interpolateString(buttonText)(selectedValues || {});
        let variantFromProps = variant;

        if (hasSeenConfirmationPage && canAutoProgressToPrefill) {
            variantFromProps = 'full_width_flex_reverse';
            label = 'Update';
        }

        const isRebrandExperiment = experimentID === experimentIDEnum.CTM_REBRAND;
        const buttonIcon = isRebrandExperiment ? 'fa-chevron-right' : icon;
        const rebrandIcon = hasSeenConfirmationPage && canAutoProgressToPrefill ? 'ctm_skinny_tick' : buttonIcon;

        return (
            <>
                <Button
                    className={buttonClassName}
                    data-id={`${this.pageName}.nextButton`}
                    icon={rebrandIcon}
                    onClick={this.onClick}
                    UIComponent={UIComponent}
                    variant={variantFromProps}
                    label={label}
                />
                {this.getLoadingAnimation()}
            </>
        );
    };

}

_NextStepButton.propTypes = {
    // nextPageName is required and used by the validateAndProgress function from mapDispatchToProps, so it still needs to
    // be on the props for the NextStepButton, even though it's not used directly inside the component itself.
    // eslint-disable-next-line react/no-unused-prop-types
    nextPageName: PropTypes.string.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    splitTestNextPage: PropTypes.string,
    // The `name` in each form for each page must match up with the "name" property in each page in page_schema.
    // Only used in mapDispatchToProps
    // eslint-disable-next-line react/no-unused-prop-types
    pageName: PropTypes.string.isRequired,
    buttonText: PropTypes.string,
    // eslint-disable-next-line react/no-unused-prop-types
    rules: PropTypes.object.isRequired,
    validateAndProgress: PropTypes.func.isRequired,
    onClickActions: PropTypes.arrayOf(PropTypes.object),
    transitioningToNewPage: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    UIComponent: PropTypes.string,
    variant: PropTypes.string,
    showLoadingAnimation: PropTypes.bool,
    hasSeenConfirmationPage: PropTypes.bool,
    isPageValid: PropTypes.bool,
    autoProgress: PropTypes.bool,
    autoProgressWithButton: PropTypes.bool,
    elementTouched: PropTypes.bool,
    dispatch: PropTypes.func,
    elementTouchName: PropTypes.string,
    selectedValues: PropTypes.object,
    className: PropTypes.string,
    /**
     * Control which icon is rendered in the button, or whether an icon is
     * rendered at all.
     *
     * {String}: Render an icon with this name.
     * null: Don't render an icon.
     */
    icon: PropTypes.string,
    splitTrafficId: PropTypes.string,
    experimentID: PropTypes.string
};

/**
 * Declaring Default Props
 * @type {Object}
 */
_NextStepButton.defaultProps = {
    buttonText: 'Next Step',
    icon: 'ctm_arrow_right'
};

/**
 * Get the IDs for elements on a page that have corresponding validation touches
 * @param {String} pageName - The current page name
 * @param {Object} state - The current state
 * @returns {Array} - The array of element IDs (relative to vertical.quote.form) on the current page, that have validation touches
 */
const getPageTouchIds = (pageName, state) => {
    const formSchemaModel = pageContext.getProps().models.formSchemaModel;
    const elements = formSchemaModel.getPageElements(pageName);
    return elements
        .filter((element) => {
            return state.getIn(['vertical', 'quote', 'validation', 'touches', element.identifier]) !== undefined;
        })
        .map((element) => element.identifier);
};
/**
 * Just trying to abstract some of the logic out of the map state to props
 * @param {String} pageName The page name
 * @param {Immutable.Map} state The state of the application
 * @returns {Boolean} True if the page is valid, False if it is invalid.
 */
const validatePage = (pageName, state) => {
    const validationModel = pageContext.getProps().models.validationModel;
    const pageRules = pageRulesStateToProps(pageName, 'validation')(state).rules;
    const visibility = state.getIn(['vertical', 'quote', 'visibility']);
    return validationModel.validatePage(pageName, pageRules, visibility).isValid;
};

/**
 * This adds the following to props:
 *  * pageRules - All of the validation rules for the elements on the current page
 *  * visibility - The mapping of element id to visibility status (true / false)
 *  * rules - The rules for the button
 *  * transitioningToNewPage - true / false if a page transition is in progress
 *
 * @param  {Object} state - state passed to the element.
 * @param  {Object} props - properties passed to the element.
 * @returns {Object} - return an object containing the rules.
 */
export const mapStateToProps = (state, props) => {
    const mappedProps = { transitioningToNewPage: get(state, 'page.transitioningToNewPage'), hasSeenConfirmationPage: get(state, 'page.hasSeenConfirmation') };
    if (get(props, 'schema.type') === 'submit') {
        return mappedProps;
    }
    mappedProps.rules = getRulesFromState(state, props);
    mappedProps.isPageValid = validatePage(props.pageName, state);
    if (props.autoProgress) {
        if (!props.elementTouchName) {
            const touchIDs = getPageTouchIds(props.pageName, state);
            if (touchIDs.length > 1 && !mappedProps.rules.autoProgress) {
                throw Error(`Auto-Progress was specified for page: ${props.pageName}, but more than one touchable element was found: ${touchIDs} -
                             this requires an autoProgress rule and elementTouchName to be defined via the schema`);
            }
            mappedProps.elementTouchName = touchIDs[0];
        } else {
            mappedProps.elementTouchName = props.elementTouchName;
        }
        mappedProps.elementTouched = state.getIn(['vertical', 'quote', 'validation', 'touches', mappedProps.elementTouchName]);
    }
    mappedProps.selectedValues = getSelectedValues(state, { schema: { selectors: props.selectors } });
    mappedProps.splitTrafficId = splitTrafficIdSelector(state);
    mappedProps.experimentID = getExperimentID(state);
    return mappedProps;
};

/**
 * @desc Dispatch a VALIDATE_AND_PROGRESS action that is caught by sagas to decide if the next page can be loaded.
 * @param {func} dispatch - The redux dispatch function
 * @param  {Object} props - properties passed to the element.
 * @param  {Object} event - the event passed to the element.
 */
const validateAndProgress = (dispatch, props, event) => {
    const pageSchemaModel = pageContext.getProps().models.pageSchemaModel;
    const apiSchema = pageSchemaModel.getApiSchemaByPageName(props.pageName);
    const progressionModel = pageContext.getProps().models.progressionModel;
    const nextPageName = interpolateString(props.nextPageName)(props.selectedValues || {});

    // rewrite api url for rego state back endpoint for A/B test
    if (apiSchema.onNext) {
        if (apiSchema.onNext.url === 'lookup/rego' && props.splitTrafficId === 'redbook_api') {
            apiSchema.onNext.url = 'lookup/redbook/rego';
        }
        if (apiSchema.onNext.url === 'lookup/redbook/rego' && props.splitTrafficId !== 'redbook_api') {
            apiSchema.onNext.url = 'lookup/rego';
        }
    }
    apiSchema.redirectResponse = excludePageFromJourney(progressionModel, pageSchemaModel, nextPageName, props);

    dispatch({
        type: 'VALIDATE_AND_PROGRESS',
        pageName: props.pageName,
        apiSchema,
        eventCB: () => {
            if (event) {
                event.preventDefault();
            }
        },
        stateKey: 'vertical.quote' // @todo get this from somewhere, should be fine for now
    });

    if (pageContext.getVertical() === 'home-contents-insurance'
        ? props.pageName === 'your_details'
        : props.pageName === 'applicant_details'
    ) {
        dispatch({ type: 'APPLICANT_OPT_IN_PRIVACY_PRESENTED' });
    }
};

/**
 * Map dispatch for VALIDATE_AND_PROGRESS action to props
 * @param  {Function} dispatch - The component dispatch method
 * @param  {Object} props - The component props
 * @returns {Object} An action creator object
 */
export const mapDispatchToProps = (dispatch, props) => {
    return {
        // eslint-disable-next-line no-shadow
        validateAndProgress: (props, event) => {
            validateAndProgress(dispatch, props, event);
        },
        dispatch
    };
};

const NextStepButton = connect(
    mapStateToProps,
    mapDispatchToProps
)(_NextStepButton);
export { _NextStepButton, NextStepButton };
